// Capa de acceso a datos del módulo Productos — 4.1 del doc de
// arquitectura: "CRUD de productos, variantes, imágenes, SEO por
// producto". Alcance MVP (15.1 del roadmap): CRUD básico + imágenes + SEO.
// Variantes (atributo/atributo_valor/producto_variante) quedan fuera de
// esta fase — la propia arquitectura-saas-tiendas-peru.md marca la primera
// versión del módulo Productos como "versión simplificada, sin variantes"
// dentro del alcance MVP; se retoma en Versión 1.0.
//
// empresa_id se toma siempre de la sesión activa (claims del JWT), nunca de
// un parámetro que reciba la vista — así es imposible que un bug de UI
// intente escribir con el empresa_id equivocado (y aunque lo intentara,
// RLS lo rechazaría igual, pero no tiene sentido ni siquiera construir esa
// consulta).

import { supabaseClient } from '@plataforma/supabase-client';

import { obtenerEstadoSesion } from '../estado-sesion.js';

function empresaIdActual() {
  return obtenerEstadoSesion().claims.empresa_id;
}

const COLUMNAS_LISTADO =
  'id, nombre, slug, precio, precio_anterior, estado, es_destacado, es_nuevo, es_oferta, orden, actualizado_en, ' +
  'imagenes:producto_imagen(url, es_principal)';

/**
 * @param {{ estado?: 'borrador' | 'publicado' | 'archivado', busqueda?: string }} [filtros]
 */
export async function listarProductos(filtros = {}) {
  let consulta = supabaseClient()
    .from('producto')
    .select(COLUMNAS_LISTADO)
    .is('eliminado_en', null)
    .order('creado_en', { ascending: false })
    // Cota de seguridad: sin esto, una empresa con un catálogo muy grande
    // traería todas las filas de una vez. No es paginación real (fuera de
    // alcance de esta fase) — solo evita una consulta sin techo.
    .limit(500);

  if (filtros.estado) consulta = consulta.eq('estado', filtros.estado);
  if (filtros.busqueda) consulta = consulta.ilike('nombre', `%${filtros.busqueda}%`);

  return consulta;
}

/** @param {string} productoId */
export async function obtenerProducto(productoId) {
  return supabaseClient()
    .from('producto')
    .select(
      '*, ' +
        'seo (*), ' +
        'imagenes:producto_imagen (id, url, es_principal, orden, texto_alternativo), ' +
        'categorias:producto_categoria (categoria_id, es_principal, categoria (id, nombre))',
    )
    .eq('id', productoId)
    .is('eliminado_en', null)
    .single();
}

/**
 * @param {object} datos
 * @param {string} datos.nombre
 * @param {string} datos.slug
 * @param {number} datos.precio
 * @param {number} [datos.precio_anterior]
 * @param {string} [datos.descripcion_corta]
 * @param {string} [datos.descripcion_larga]
 */
export async function crearProducto(datos) {
  return supabaseClient()
    .from('producto')
    .insert({ ...datos, empresa_id: empresaIdActual() })
    .select('id, seo_id')
    .single();
}

/**
 * @param {string} productoId
 * @param {object} cambios
 */
export async function actualizarProducto(productoId, cambios) {
  return supabaseClient().from('producto').update(cambios).eq('id', productoId).select().single();
}

/**
 * Eliminación lógica — ver 7.4 del doc de BD (política uniforme de soft
 * delete). No se borra físicamente ni siquiera desde el panel: RLS ya
 * filtra `eliminado_en is null` en todas las lecturas de listado.
 *
 * @param {string} productoId
 */
export async function eliminarProducto(productoId) {
  return supabaseClient()
    .from('producto')
    .update({ eliminado_en: new Date().toISOString() })
    .eq('id', productoId);
}

/**
 * Reemplaza por completo el set de categorías de un producto (delete +
 * insert dentro de una única llamada lógica). Se hace en dos pasos desde el
 * cliente en vez de con una función SQL dedicada porque el volumen por
 * producto es mínimo (unas pocas categorías) y no hay condición de carrera
 * real: solo un editor a la vez edita un producto dado en la práctica de
 * una tienda pequeña — si eso cambia, es candidato a moverse a una función
 * SQL transaccional.
 *
 * @param {string} productoId
 * @param {{ categoriaId: string, esPrincipal: boolean }[]} categorias
 */
export async function asignarCategoriasProducto(productoId, categorias) {
  const cliente = supabaseClient();

  const { error: errorBorrado } = await cliente
    .from('producto_categoria')
    .delete()
    .eq('producto_id', productoId);
  if (errorBorrado) return { error: errorBorrado };

  if (categorias.length === 0) return { error: null };

  return cliente.from('producto_categoria').insert(
    categorias.map((c) => ({
      producto_id: productoId,
      categoria_id: c.categoriaId,
      es_principal: c.esPrincipal,
    })),
  );
}

/**
 * @param {string} seoId
 * @param {object} cambios
 */
export async function actualizarSeoProducto(seoId, cambios) {
  return supabaseClient().from('seo').update(cambios).eq('id', seoId).select().single();
}

/**
 * Conteo de productos no eliminados (de cualquier estado: borrador,
 * publicado o archivado) — para la barra de límite de plan (UX, no
 * seguridad — ver ADR-0007) y para el dashboard.
 */
export async function contarProductos() {
  return supabaseClient()
    .from('producto')
    .select('id', { count: 'exact', head: true })
    .is('eliminado_en', null);
}
